import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Loader2 } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRequireResetPassword } from '../hooks/useAuth';
import { ForgotPasswordRequest } from '../models/Auth';

interface ForgotPasswordFormProps {
  onBack?: () => void;
  className?: string;
}

export const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ onBack, className = '' }) => {
  const { t } = useTranslation();
  const requireResetMutation = useRequireResetPassword();
  const [isSuccess, setIsSuccess] = useState(false);

  const [formData, setFormData] = useState<ForgotPasswordRequest>({
    email: '',
  });

  const [fieldErrors, setFieldErrors] = useState<{
    email?: string;
  }>({});

  // 🎯 Validación de campo email
  const validateField = (value: string): string | undefined => {
    if (!value.trim()) return 'El email es requerido';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return 'El formato del email no es válido';
    return undefined;
  };

  // 🎯 Manejo de cambios en input
  const handleInputChange = (value: string) => {
    setFormData((prev) => ({ ...prev, email: value }));

    // Limpiar error del campo si existe
    if (fieldErrors.email) {
      setFieldErrors((prev) => ({ ...prev, email: undefined }));
    }
  };

  // 🎯 Validación completa del formulario
  const validateForm = (): boolean => {
    const emailError = validateField(formData.email);
    if (emailError) {
      setFieldErrors({ email: emailError });
      return false;
    }
    return true;
  };

  // 🎯 Manejo del submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      await requireResetMutation.mutateAsync(formData);
      setIsSuccess(true);
    } catch (error) {
      // Los errores se manejan automáticamente en el hook
      console.error('Error en forgot password:', error);
    }
  };

  // 🎯 Manejo de blur para validación inmediata
  const handleBlur = () => {
    const error = validateField(formData.email);
    if (error) {
      setFieldErrors((prev) => ({ ...prev, email: error }));
    }
  };

  // Si es exitoso, mostrar mensaje de éxito
  if (isSuccess) {
    return (
      <div className={`max-w-96 ${className}`}>
        <div className="space-y-4">
          <div className="p-4 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-md">
            <p className="text-sm text-green-700 dark:text-green-300">
              {t(
                'auth.forgotPassword.success',
                'Se ha enviado un enlace a tu correo electrónico. Por favor, revisa tu bandeja de entrada.',
              )}
            </p>
          </div>
          {onBack && (
            <Button type="button" variant="outline" className="w-full" onClick={onBack}>
              {t('auth.forgotPassword.backToLogin', 'Volver al login')}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`max-w-96 ${className}`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* 🎯 Error general */}
        {requireResetMutation.error && (
          <p className="text-sm text-destructive">
            {requireResetMutation.error.message || 'Error al solicitar el restablecimiento de contraseña'}
          </p>
        )}

        {/* 🎯 Campo Email */}
        <div className="space-y-2">
          <label htmlFor="forgot-email" className="text-sm font-medium text-foreground">
            {t('form.email.name', 'Email')}
          </label>
          <Input
            id="forgot-email"
            type="email"
            placeholder={t('form.email.placeholder', 'tu@email.com')}
            value={formData.email}
            onChange={(e) => handleInputChange(e.target.value)}
            onBlur={handleBlur}
            className={`${fieldErrors.email ? 'border-destructive' : ''}`}
            autoFocus
            autoComplete="email"
            data-testid="forgot-password-email"
          />
          {fieldErrors.email && <p className="text-sm text-destructive">{fieldErrors.email}</p>}
        </div>

        {/* 🎯 Botón de envío */}
        <div className="pt-4">
          <Button
            type="submit"
            className="w-full"
            disabled={requireResetMutation.isPending}
            data-testid="forgot-password-submit"
          >
            {requireResetMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('auth.forgotPassword.loading', 'Enviando enlace...')}
              </>
            ) : (
              t('auth.forgotPassword.submitButton', 'Enviar Enlace')
            )}
          </Button>
        </div>

        {/* 🎯 Botón para volver */}
        {onBack && (
          <div className="pt-2">
            <Button type="button" variant="ghost" className="w-full" onClick={onBack}>
              {t('auth.forgotPassword.backToLogin', 'Volver al login')}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
};

export default ForgotPasswordForm;
